import { logUser } from 'logger';
import { DateTime } from 'luxon';

import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import { createCommandFactory, createProcess } from '@dojo/framework/stores/process';
import { ReplacePatchOperation } from '@dojo/framework/stores/state/Patch';
import { add, replace } from '@dojo/framework/stores/state/operations';
// import { tgfkeys } from './support/loadroots';
import { delay } from '@holmescorp/common';
import { XAPI } from '@holmescorp/xapi';

import { lccstore } from './base';
import {
	LPView,
	ResourceTracking,
	ServerConfig,
	SiteMeta,
	SiteStatus,
	Stub,
	TopicDef,
	TopicRoots,
	Tracking,
	View
} from './interfaces';

const createCommand = createCommandFactory<any, SiteMeta>();

// -----
// Set up the initial values in the store.
const initialStateCommand = createCommand(({ path }) => {
	let courseid: number = 0;
	let coursename: string = '';
	let productname: string = '';

	const { course_session, course_product, sitealias, portal_site, association_name: association } = window.hcstatic;

	if (course_session && course_session.id) {
		courseid = course_session.id;
		coursename = course_session.title;
	}
	if (course_product && course_product.title) {
		productname = course_product.title;
	}

	const product = sitealias || portal_site || '';

	return [
		add(path('online'), false),
		add(path('title'), association),
		add(path('product'), product),
		add(path('viewStack'), []),
		add(path('viewIndex'), -1),
		add(path('trackingStack'), []),
		add(path('quizRunning'), 0),
		add(path('searchOpen'), false),
		add(path('exploreAllOpen'), false),
		add(path('ditaroots'), []),
		add(path('accordionSelected'), []),
		add(path('staticprops'), { coursename, courseid, productname }),
		add(path('mainmenu'), []),
		add(path('logintime'), ''),
		add(path('logoutTime'), ''),
		add(path('actor_xapi'), ''),
		add(path('expanded_topic'), '')
	];
});

const initialStateProcess = createProcess('initial-state', [initialStateCommand]);
initialStateProcess(lccstore)({} as any as SiteMeta);

// -----
//
const serverConfigChangeCommand = createCommand<{ config: ServerConfig }>(({ path, payload: { config } }) => {
	return [
		add(path('bookversion'), config.bookversion),
		add(path('quizpatterns'), config.quizpatterns),
		add(path('mainmenu'), config.mainmenu)
	];
});

export const serverConfigChangeProcess = createProcess('siteconfig-change', [serverConfigChangeCommand]);
export const serverConfigChange = serverConfigChangeProcess(lccstore);

// -----
// Fired on change in online status.
const onlineChangeCommand = createCommand<{ online: boolean }>(({ path, payload: { online } }) => {
	const operations = [replace(path('online'), online)];
	return operations;
});

export const onlineChangeProcess = createProcess('online-change', [onlineChangeCommand]);
export const onlineChange = onlineChangeProcess(lccstore);

// -----
// Fired on change in sitestatus.
const sitestatusChangeCommand = createCommand<{ sitestatus: SiteStatus }>(({ path, payload: { sitestatus } }) => {
	const actor = new XAPI.Agent({
		objectType: 'Agent',
		name: `${sitestatus.user_id}`,
		sessionid: `${window.hcstatic.course_session?.pk || 0}`,
		mbox: `${sitestatus.user_id}@example.com`
	});
	const operations = [replace(path('sitestatus'), sitestatus), replace(path('actor_xapi'), actor)];
	return operations;
});

export const sitestatusChangeProcess = createProcess('sitestatus-change', [sitestatusChangeCommand]);
export const sitestatusChange = sitestatusChangeProcess(lccstore);

// -----
// Fired on change in ditaroots.
const ditarootsChangeCommand = createCommand<{ ditaroots: string[] }>(({ path, payload: { ditaroots } }) => {
	return [replace(path('ditaroots'), ditaroots)];
});

export const ditarootsChangeProcess = createProcess('ditaroots-change', [ditarootsChangeCommand]);
export const ditarootsChange = ditarootsChangeProcess(lccstore);

// -----
// Fired on change to root topics.
const topicsChangeCommand = createCommand<{ topics: TopicRoots }>(({ path, payload: { topics } }) => {
	const operations = [replace(path('topics'), topics)];
	return operations;
});

export const topicsChangeProcess = createProcess('topics-change', [topicsChangeCommand]);
export const topicsChange = topicsChangeProcess(lccstore);

// -----
// Fired on change to topic.
const topicChangeCommand = createCommand<{ topic: TopicDef }>(({ path, payload: { topic } }) => {
	const operations = [replace(path('topic'), topic)];
	return operations;
});

export const topicChangeProcess = createProcess('topic-change', [topicChangeCommand]);
export const topicChange = topicChangeProcess(lccstore);

// -----
// Fired on topic stub change.
const topicStubChangeCommand = createCommand<{ stub: Stub }>(({ path, payload: { stub } }) => {
	const { topicname } = stub;
	const operations = [replace(path('stubs', topicname), stub)];
	return operations;
});

export const topicStubChangeProcess = createProcess('topicstub-change', [topicStubChangeCommand]);
export const topicStubChange = topicStubChangeProcess(lccstore);

// -----
// Fired on lpview change.
const lpviewChangeCommand = createCommand<{ lpview: LPView }>(({ path, payload: { lpview } }) => {
	const { widget } = lpview;
	const view: View = {
		widget,
		params: { ...lpview }
	};
	const operations = [replace(path('view'), view)];
	return operations;
});

export const lpviewChangeProcess = createProcess('lpview-change', [lpviewChangeCommand]);
export const lpviewChange = lpviewChangeProcess(lccstore);

// -----
// Fired on view change / edit.
const viewChangeCommand = createCommand<{ view: View }>(({ path, payload: { view } }) => {
	const operations = [];
	if (view.params && view.params.expandKey) {
		operations.push(replace(path('accordionSelected'), view.params.expandKey));
	}
	operations.push(replace(path('view'), view));
	return operations;
});

const viewEditCommand = createCommand<{ view: Partial<View> }>(({ path, get, payload: { view } }) => {
	const editview = get(path('view'));
	const { params: viewparams = {} } = view;
	Object.keys(viewparams).forEach((nm) => {
		editview.params[nm] = viewparams[nm];
	});
	const operations = [replace(path('view'), editview)];
	return operations;
});

// eslint-disable-next-line @typescript-eslint/ban-types
const viewClearCommand = createCommand<{}>(({ path }) => {
	return [replace(path('view'), null)];
});

const viewResetCommand = createCommand<{ view: View }>(({ path }) => {
	return [replace(path('viewStack'), []), replace(path('viewIndex'), -1)];
});

export const viewChangeProcess = createProcess('view-change', [viewChangeCommand]);
export const viewChange = viewChangeProcess(lccstore);
export const viewClearProcess = createProcess('view-clear', [viewClearCommand]);
export const clearView = (): void => {
	viewClearProcess(lccstore)({});
};
export const viewEditProcess = createProcess('view-edit', [viewEditCommand]);
export const viewEdit = viewEditProcess(lccstore);
export const viewResetProcess = createProcess('view-reset', [viewResetCommand, viewChangeCommand]);

export const resetView = (view: View): void => {
	viewResetProcess(lccstore)({ view });
};

// -----
const whichActiveViewCommand = createCommand<{ whichActive: 'assess' | 'study' | 'practice' | null }>(
	({ path, payload: { whichActive } }) => {
		return [replace(path('whichActiveView'), whichActive)];
	}
);

export const whichActiveViewProcess = createProcess('which-active-view', [whichActiveViewCommand]);
export const whichActiveView = whichActiveViewProcess(lccstore);

// -----
// Fired on topic-accordion pane change
const tapaneChangeCommand = createCommand<{ accordionSelected: string | string[] | undefined }>(
	({ path, payload: { accordionSelected } }) => {
		if (accordionSelected !== undefined && !Array.isArray(accordionSelected)) {
			accordionSelected = [accordionSelected];
		}
		return [replace(path('accordionSelected'), accordionSelected)];
	}
);

export const tapaneChangeProcess = createProcess('accordion-pane-change', [tapaneChangeCommand]);
export const tapaneChange = tapaneChangeProcess(lccstore);

// -----
// Fired on LPL change.
const lplChangeCommand = createCommand<{ lpview: LPView }>(({ get, path, payload: { lpview } }) => {
	const priorview: LPView = get(path('defaultLPView'));
	let operations: ReplacePatchOperation<any, any>[] = [];
	if (!priorview || priorview.view !== lpview.view) {
		operations = [replace(path('defaultLPView'), lpview)];
	}
	return operations;
});

export const lplChangeProcess = createProcess('view-change', [lplChangeCommand]);
export const lplChange = lplChangeProcess(lccstore);

// -----
// Tracking
const trackingChangeCommand = createCommand<{ tracking: Partial<Tracking> }>(({ path, get, payload: { tracking } }) => {
	const trackingChg = get(path('tracking')) || { id: 0 };
	Object.keys(tracking).forEach((k) => {
		trackingChg[k] = (tracking as any)[k];
	});
	trackingChg.id += 1;
	const { topichead, topicset, topicgroup, topic } = trackingChg;
	const location = [topichead, topicset, topicgroup, topic].filter((v) => !!v).join('/');
	logUser.info(() => `(tracking) go to /explore/${location}`);
	return [replace(path('tracking'), trackingChg)];
});

export const trackingChangeProcess = createProcess('tracking-change', [trackingChangeCommand]);
export const trackingChange = trackingChangeProcess(lccstore);

const logintimeChangeCommand = createCommand<{ logintime: string }>(({ path, payload: { logintime } }) => {
	return replace(path('logintime'), logintime);
});

export const logintimeChangeProcess = createProcess('logintime-change', [logintimeChangeCommand]);
export const logintimeChange = logintimeChangeProcess(lccstore);

export const loginDuration = (): string => {
	const { get, path } = lccstore;
	const logintimeStr = get(path('logintime'));
	const startDate = DateTime.fromISO(logintimeStr);
	const endDate = DateTime.now();
	const diffStr = endDate.diff(startDate).normalize().toISO();
	logUser.info((): string => `(logout) login duration: ${diffStr}`);
	console.info(`(logout) login duration: ${diffStr}`);
	return diffStr;
};

const logoutTimeChangeCommand = createCommand<{ logoutTime: string }>(({ path, payload: { logoutTime } }) => {
	return replace(path('logoutTime'), logoutTime);
});

export const logoutTimeChangeProcess = createProcess('logoutTime-change', [logoutTimeChangeCommand]);
export const logoutTimeChange = logoutTimeChangeProcess(lccstore);

const internalTrackingChangeCommand = createCommand<{ tracking: Tracking }>(({ path, get, payload: { tracking } }) => {
	const trackingChg = get(path('tracking')) || { id: 0 };
	tracking.id = trackingChg.id + 1;
	return [replace(path('tracking'), tracking)];
});

export const internalTrackingChangeProcess = createProcess('internal-tracking-change', [internalTrackingChangeCommand]);
export const internalTrackingChange = internalTrackingChangeProcess(lccstore);

// Resource Tracking
const resourceTrackingChangeCommand = createCommand<{ tracking: ResourceTracking }>(
	({ path, payload: { tracking } }) => {
		return [replace(path('resourceTracking'), tracking)];
	}
);

export const resourceTrackingChangeProcess = createProcess('resource-tracking-change', [resourceTrackingChangeCommand]);
export const resourceTrackingChange = resourceTrackingChangeProcess(lccstore);

// -----
const staticpropsChangeCommand = createCommand<{ coursename: string }>(({ path, payload: { coursename } }) => {
	return [replace(path('staticprops', 'coursename'), coursename)];
});

export const staticpropsChangeProcess = createProcess('staticprops-change', [staticpropsChangeCommand]);

const setupStaticprops = (dynls: Map<string, Bundle<Messages>>) => {
	const { get, path } = lccstore;
	const currentval = get(path('staticprops', 'coursename'));
	const {
		messages: { self_study = '' }
	} = dynls.get('settings') || { messages: {} };

	if (currentval === '' && self_study !== '') {
		staticpropsChangeProcess(lccstore)({ coursename: self_study });
	}
};

// -----
// Dynamic NLS
const dynlsAddCommand = createCommand<{ dynls: Map<string, Bundle<Messages>> }>(({ path, payload: { dynls } }) => {
	(async () => {
		await delay(50);
		setupStaticprops(dynls);
	})();

	return [add(path('dynls'), dynls)];
});

const dynlsChangeCommand = createCommand<{ dynls: Map<string, Bundle<Messages>> }>(({ path, payload: { dynls } }) => {
	return [replace(path('dynls'), dynls)];
});

export const dynlsAddProcess = createProcess('dynls-add', [dynlsAddCommand]);
export const dynlsChangeProcess = createProcess('dynl-change', [dynlsChangeCommand]);
export const dynlsAdd = dynlsAddProcess(lccstore);
export const dynlsChange = dynlsChangeProcess(lccstore);

// -----
// quiz running flag
const quizRunningCommand = createCommand<{ quizRunning: boolean }>(({ get, path, payload: { quizRunning } }) => {
	let count = get(path('quizRunning'));
	count = count + (quizRunning ? 1 : -1);
	if (count < 0) {
		count = 0;
	}
	return [replace(path('quizRunning'), count)];
});

export const quizRunningProcess = createProcess('quizrunning-change', [quizRunningCommand]);
export const quizIsRunning = quizRunningProcess(lccstore);

// -----
// search open flag
const searchOpenCommand = createCommand<{ searchOpen: boolean }>(({ path, payload: { searchOpen } }) => {
	logUser.info(() => `${searchOpen ? 'open' : 'close'} search-pane`);
	return [replace(path('searchOpen'), searchOpen)];
});

export const searchOpenProcess = createProcess('searchopen-change', [searchOpenCommand]);
export const searchOpen = searchOpenProcess(lccstore);

// -----
// explore-all open flag
const exploreAllOpenCommand = createCommand<{ exploreAllOpen: boolean }>(({ path, payload: { exploreAllOpen } }) => {
	logUser.info(() => `${exploreAllOpen ? 'open' : 'close'} explore-all`);
	return [replace(path('exploreAllOpen'), exploreAllOpen)];
});

export const exploreAllOpenProcess = createProcess('explore-all-open-change', [exploreAllOpenCommand]);
export const exploreAllOpen = exploreAllOpenProcess(lccstore);

// -----
// explore-all scroll-to flag
const exploreAllScrollCommand = createCommand<{ exploreAllScroll: string }>(
	({ path, payload: { exploreAllScroll } }) => {
		exploreAllScroll.length > 0 && logUser.info(() => `explore-all scroll to ${exploreAllScroll}`);
		return [replace(path('exploreAllScroll'), exploreAllScroll)];
	}
);

export const exploreAllScrollProcess = createProcess('explore-all-open-change', [exploreAllScrollCommand]);
export const exploreAllScroll = exploreAllScrollProcess(lccstore);

const documentTitleUpdateCommand = createCommand<{ title: string }>(({ path, payload: { title } }) => {
	title.length > 0 && logUser.info(() => `document.title to ${title}`);
	return [replace(path('title'), title)];
});

export const documentTitleUpdateProcess = createProcess('document-title-change', [documentTitleUpdateCommand]);
export const documentTitleUpdate = documentTitleUpdateProcess(lccstore);

// -----
// Update expanded topic
const expandedTopicChangeCommand = createCommand<{ topicid: string }>(({ path, payload: { topicid } }) => {
	return [replace(path('expanded_topic'), topicid)];
});

export const expandedTopicChangeProcess = createProcess('expandedTopic-change', [expandedTopicChangeCommand]);
export const expandedTopicChange = expandedTopicChangeProcess(lccstore);
